import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import WhatsAppButton from './components/common/WhatsAppButton';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const OurDesign = lazy(() => import('./pages/OurDesign'));
const Services = lazy(() => import('./pages/Services'));
const HowWeWork = lazy(() => import('./pages/HowWeWork'));
const Contact = lazy(() => import('./pages/Contact'));
const LandingConsultation = lazy(() => import('./pages/LandingConsultation'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminHero = lazy(() => import('./pages/admin/AdminHero'));
const AdminAbout = lazy(() => import('./pages/admin/AdminAbout'));
const AdminPortfolio = lazy(() => import('./pages/admin/AdminPortfolio'));
const AdminDesigns = lazy(() => import('./pages/admin/AdminDesigns'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminHowWeWork = lazy(() => import('./pages/admin/AdminHowWeWork'));
const AdminContacts = lazy(() => import('./pages/admin/AdminContacts'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
};

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
    <WhatsAppButton />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ duration: 4000, style: { fontFamily: 'Jost, sans-serif' } }} />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/portfolio" element={<PublicLayout><Portfolio /></PublicLayout>} />
              <Route path="/our-design" element={<PublicLayout><OurDesign /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
              <Route path="/how-we-work" element={<PublicLayout><HowWeWork /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />

              {/* Ad landing page (Google / Facebook Ads) — standalone, no Navbar/Footer */}
              <Route path="/free-consultation" element={<LandingConsultation />} />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/hero" element={<ProtectedRoute><AdminHero /></ProtectedRoute>} />
              <Route path="/admin/about" element={<ProtectedRoute><AdminAbout /></ProtectedRoute>} />
              <Route path="/admin/portfolio" element={<ProtectedRoute><AdminPortfolio /></ProtectedRoute>} />
              <Route path="/admin/designs" element={<ProtectedRoute><AdminDesigns /></ProtectedRoute>} />
              <Route path="/admin/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
              <Route path="/admin/how-we-work" element={<ProtectedRoute><AdminHowWeWork /></ProtectedRoute>} />
              <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
              <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
